import React, { useState, useRef } from "react"
import { VariantReader } from "aldoo-ra/VariantReader"
import Typography from "aldoo-ra/Typography"
import LocalUserPreferences from "aldoo-ra/LocalUserPreferences"
import { twMerge } from "tailwind-merge"
import { ChevronUpIcon } from "@heroicons/react/24/outline"

/**
 * Panel component with collapsible functionality
 *
 * @param {Object} props
 * @param {string} [props.variant="default"] - The variant of the panel
 * @param {string|React.ReactNode} [props.title] - The title to display in the panel header
 * @param {boolean} [props.defaultOpen=true] - Whether the panel should be open by default
 * @param {boolean} [props.forceOpen] - Hint to auto-expand panel (doesn't prevent manual toggling)
 * @param {boolean} [props.collapsible=true] - Whether the panel can be collapsed
 * @param {boolean} [props.scrollable=true] - Whether the body content should be scrollable
 * @param {boolean} [props.animated=true] - Whether to animate the panel when expanding/collapsing
 * @param {string|null} [props.maxHeight=null] - Maximum height for panel body (tailwind sizing class without the 'max-h-' prefix)
 * @param {React.ReactNode} props.children - The content of the panel
 * @param {Object} [props.styleOverrides] - Style overrides for the panel
 * @param {string} [props.className] - Additional CSS classes for the panel container
 * @param {string} [props.headerClassName] - Additional CSS classes for the header
 * @param {string} [props.bodyClassName] - Additional CSS classes for the body
 * @param {string} [props.titleClassName] - Additional CSS classes for the title (overrides variant's titleClassName)
 * @param {React.ReactNode} [props.headerContent] - Additional content to display on the right side of the header
 * @param {Function} [props.onToggle] - Callback when the panel is toggled
 * @param {string|null} [props.localUserPreferencesKey] - Key for storing user preferences
 *
 * @note Title styling can be customized through variants using:
 * - titleVariant: Typography variant for the title (e.g. "admin_h4", "admin_body")
 * - titleClassName: Additional classes for the title
 */
export default function Panel({
  variant = "default",
  title,
  defaultOpen = true,
  forceOpen,
  collapsible = true,
  children,
  styleOverrides,
  className = "",
  headerClassName = "",
  bodyClassName = "",
  titleClassName = "",
  headerContent,
  scrollable = true,
  maxHeight = null,
  animated = true,
  onToggle,
  localUserPreferencesKey = null, // Key for storing user preferences
}) {
  const [isOpen, setIsOpen] = useState(() => {
    // Try to load from user preferences first, then fall back to defaultOpen
    if (localUserPreferencesKey && localUserPreferencesKey.trim()) {
      try {
        const savedState = LocalUserPreferences.get(localUserPreferencesKey)
        if (savedState !== undefined && typeof savedState === 'boolean') {
          return savedState
        }
      } catch (error) {
        console.warn("Failed to load panel preference:", error)
      }
    }
    return defaultOpen
  })

  // Auto-expand when forceOpen becomes true, but don't auto-collapse
  React.useEffect(() => {
    if (forceOpen === true && !isOpen) {
      setIsOpen(true)
    }
  }, [forceOpen, isOpen])
  const headerRef = useRef(null)

  const variants = VariantReader.useVariant({
    componentName: "Panel",
    variant,
    styleOverrides,
  })

  const handleToggle = () => {
    if (!collapsible) return

    const newState = !isOpen
    setIsOpen(newState)

    // Save to local user preferences if key is provided
    if (localUserPreferencesKey && localUserPreferencesKey.trim()) {
      try {
        LocalUserPreferences.set(localUserPreferencesKey, newState)
      } catch (error) {
        console.warn("Failed to save panel preference:", error)
      }
    }

    if (onToggle) {
      onToggle(newState)
    }
  }

  // Default styles if not provided by VariantReader
  const defaultStyles = {
    panel: "border border-admin_bordered rounded-md overflow-hidden",
    header: "flex justify-between items-center p-4 bg-green-800 text-white cursor-pointer",
    body: "p-4 bg-admin_paper",
    icon: "transition-transform duration-200 ease-in-out",
    titleVariant: "admin_h4", // Default Typography variant for title
    titleClassName: "", // Default additional classes for title
  }

  // Use VariantReader styles or fallback to defaults
  const styles = {
    panel: variants?.panel || defaultStyles.panel,
    panelOpen: variants?.panelOpen || variants?.panel || defaultStyles.panel,
    header: variants?.header || defaultStyles.header,
    body: variants?.body || defaultStyles.body,
    icon: variants?.icon || defaultStyles.icon,
    titleVariant: variants?.titleVariant || defaultStyles.titleVariant,
    titleClassName: variants?.titleClassName || defaultStyles.titleClassName,
  }

  // Style for panel container
  // Adding flex-shrink-0 to ensure collapsed panels don't shrink
  // Adding h-auto to ensure the panel takes its natural height when collapsed
  const panelStyles = twMerge(
    isOpen ? styles.panelOpen : styles.panel,
    isOpen ? "flex flex-col flex-1" : "flex-shrink-0 h-auto",
    animated && "transition-all duration-300",
    className
  )

  // Give the header a fixed min-height to ensure consistency
  const headerStyles = twMerge(
    styles.header,
    "flex-shrink-0 h-16", // Fixed height for all headers
    headerClassName
  )

  // For the body, if open, allow it to flex and scroll
  // If maxHeight is specified, use it, otherwise limit to a reasonable height
  const maxHeightClass = maxHeight ? `max-h-${maxHeight}` : "max-h-96";
  const bodyStyles = twMerge(
    styles.body,
    scrollable && "overflow-y-auto",
    isOpen ? "flex-1" : "hidden",
    isOpen && scrollable ? maxHeightClass : "",
    bodyClassName
  )

  // Fixed arrow rotation - use a single arrow SVG and rotate it with transition
  const arrowStyle = twMerge(
    styles.icon,
    "transform transition-transform duration-200 ease-in-out",
    isOpen ? "rotate-0" : "rotate-180"
  )

  return (
    <div className={panelStyles}>
      <div
        ref={headerRef}
        className={headerStyles}
        onClick={handleToggle}
      >
        <div className="flex items-center gap-2">
          {collapsible && (
            <div className={arrowStyle}>
              <ChevronUpIcon className="w-5 h-5" />
            </div>
          )}
          
          {typeof title === "string" ? (
            <Typography
              variant={styles.titleVariant}
              className={twMerge(styles.titleClassName, titleClassName)}
            >
              {title}
            </Typography>
          ) : (
            title
          )}
        </div>

        {headerContent && (
          <div className="flex items-center gap-2 ml-auto">
            {headerContent}
          </div>
        )}
      </div>

      {isOpen && <div className={bodyStyles}>{children}</div>}
    </div>
  )
}